import { Fa, TableColumnType } from '@eltoro-ui/components'
import classNames from 'classnames'
import { EmptyField, TableRowLoadingIcon } from 'Components'
import { formatDate } from 'Helpers'
import { PopulatedSubscriptionRow } from './SubscriptionTable'

export const getSubscriptionTableColumns = (partner?: boolean) => {
  const nameColumn: TableColumnType<PopulatedSubscriptionRow> = {
    path: 'name',
    label: 'Subscription Name',
    removeFilter: true,
    RowCell: ({ name, locked, hidden }) => (
      <div>
        {locked && (
          <span data-tooltip="right" aria-label="This pixel is locked.">
            <Fa icon="lock" size={1} />
          </span>
        )}
        {hidden && (
          <span data-tooltip="right" aria-label="This pixel is hidden.">
            <Fa icon="user-secret" size={1} />
          </span>
        )}
        {name || 'N/A'}
      </div>
    ),
  }

  const pixelNameColumn: TableColumnType<PopulatedSubscriptionRow> = {
    path: ['pixel', 'name'],
    label: 'Pixel Name',
    removeFilter: true,
    removeSort: true,
    RowCell: ({ pixelName, loading }) => {
      if (loading) return <TableRowLoadingIcon />
      return pixelName || <EmptyField />
    },
  }

  const countColumn: TableColumnType<PopulatedSubscriptionRow> = {
    path: '',
    label: 'Count',
    removeFilter: true,
    removeSort: true,
    RowCell: ({ latestStats }) =>
      latestStats?.matches === undefined ? <EmptyField /> : latestStats.matches,
  }

  // "Created": Default status upon creation
  // "Ready": Daily processing is on but no publisher is assigned
  // "Active": Daily processing is on and a publisher is assigned
  // This should apply to all subscriptions, including migrated ones.
  const statusColumn: TableColumnType<PopulatedSubscriptionRow> = {
    path: '',
    label: 'Status',
    removeFilter: true,
    removeSort: true,
    RowCell: ({ enabled, publisherId }) => {
      const status = (() => {
        if (enabled && !publisherId) return 'Ready'
        if (enabled && publisherId) return 'Active'
        return 'Created'
      })()
      return (
        <div
          className={classNames(
            'max-w-min rounded-full py-1 px-3 text-xs font-bold',
            {
              'text-base-info bg-base-info-50': status === 'Ready',
              'text-success-500 bg-success-50': status === 'Active',
              'bg-warning-50 text-warning-500': status === 'Created',
            },
          )}
        >
          {status}
        </div>
      )
    },
  }

  const tagsColumn: TableColumnType<PopulatedSubscriptionRow> = {
    path: 'tags',
    label: 'Tags',
    removeSort: true,
    RowCell: ({ tags }) => {
      if (!tags || tags.length === 0) return <EmptyField />
      return (
        <div className="flex flex-wrap gap-1">
          {tags.slice(0, 3).map((tag, index) => (
            <span
              key={index}
              className="bg-primary-50 text-primary-700 inline-block rounded-full px-2 py-1 !text-xs font-bold"
            >
              {tag}
            </span>
          ))}
          {tags.length > 3 && (
            <span
              className="!text-xs font-bold text-gray-500 cursor-help"
              data-tooltip="right"
              aria-label={tags.slice(3).join(', ')}
            >
              +{tags.length - 3} more
            </span>
          )}
        </div>
      )
    },
  }

  const dateCreatedColumn: TableColumnType<PopulatedSubscriptionRow> = {
    path: 'created',
    label: 'Date Created',
    removeFilter: true,
    RowCell: (row) => {
      if (!row.created) return <EmptyField />
      return formatDate(row.created)
    },
  }

  const idColumn: TableColumnType<PopulatedSubscriptionRow> = {
    path: 'id',
    label: 'Subscription ID',
    removeFilter: true,
    removeSort: true,
  }

  // partner-specific columns
  const customerNameColumn: TableColumnType<PopulatedSubscriptionRow> = {
    path: 'orgName',
    label: 'Customer Name',
    removeFilter: true,
    removeSort: true,
    // i think this might only show up on partner only api subscriptions
    RowCell: ({ orgName }) => orgName || <EmptyField />,
  }

  const yesterdayCountColumn: TableColumnType<PopulatedSubscriptionRow> = {
    path: '',
    label: "Yesterday's Counts",
    removeFilter: true,
    removeSort: true,
    // TODO: not sure what this is
    RowCell: ({ latestStats }) => <EmptyField />,
  }

  const columns: TableColumnType<PopulatedSubscriptionRow>[] = partner
    ? [
        nameColumn,
        customerNameColumn,
        yesterdayCountColumn,
        statusColumn,
        dateCreatedColumn,
        pixelNameColumn,
      ]
    : [
        nameColumn,
        pixelNameColumn,
        countColumn,
        tagsColumn,
        statusColumn,
        dateCreatedColumn,
        idColumn,
      ]

  return columns
}
